import React, { useEffect, useRef, useState } from 'react'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'

export interface HelpTooltipButtonProps {
  title: string
  description: string
  /** Short text shown on hover via native title when popover is closed. */
  hoverHint?: string
}

export default function HelpTooltipButton({ title, description, hoverHint }: HelpTooltipButtonProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <span
      ref={rootRef}
      className="absolute right-2 top-2 z-10"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="rounded-full p-0.5 text-heritage-grey transition-colors hover:bg-white/80 hover:text-blueprint-blue"
        aria-label={`Help: ${title}`}
        aria-expanded={open}
        title={hoverHint ?? title}
        onClick={() => setOpen((v) => !v)}
        data-testid={`help-tooltip-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <QuestionMarkCircleIcon className="h-4 w-4" />
      </button>
      {open ? (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border border-[#E2E5EA] bg-white p-3 text-left shadow-large ring-1 ring-black/5"
        >
          <p className="text-xs font-semibold text-blueprint-blue">{title}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-heritage-grey">{description}</p>
        </div>
      ) : null}
    </span>
  )
}
